import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { isRecord } from '@/utils/objects';
import { dsBrandDocument, type DsBrandDocument } from '../ds-brand-document/entity';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import {
  dsGlobalStylesKeys,
  parseDsGlobalStyles,
  transformGlobalStylesReferences,
  type DsGlobalStyles,
  type DsGlobalStylesProcessStatusResponse,
  type GlobalStylesReference,
} from './entity';

/**
 * Design Studio global styles read queries.
 *
 * Each read follows the bulletproof-react triplet (plain async fn +
 * queryOptions factory + thin hook). The status polls are exposed as
 * non-suspense `useQuery` hooks whose options factory the caller composes with
 * its own `refetchInterval` / `enabled` — polling is intentionally NOT baked in
 * here (parcel polls every 2500ms; see `use-ai-context` / `use-multi-source-extract`).
 */

const STALE_TIME = 60_000;

/* ------------------------------------------------------------------------- */
/* getDsGlobalStyles — the workspace global styles object                      */
/* ------------------------------------------------------------------------- */

/**
 * The parsed GET body, normalized to a `{ styles, brand }` envelope whether the
 * API returned that composite shape, a bare token tree, or `null`.
 *
 * This is what the query caches. Keeping the normalized envelope intact lets
 * every surface select its slice off one query — one fetch, one cache slot — so
 * the Global Styles page's styles grid and Brand section share the same parsed
 * boundary.
 */
export async function getDsGlobalStylesComposite(workspaceId: number): Promise<DsGlobalStylesEnvelope> {
  const raw = await hydraUiApi.dsGlobalStyles.getDsGlobalStyles({
    params: { path: { environment_id: String(workspaceId) } },
  });
  return parseDsGlobalStylesEnvelope(raw);
}

/** The parsed composite: both slices, each `null` when absent. */
export interface DsGlobalStylesEnvelope {
  styles: DsGlobalStyles | null;
  brand: DsBrandDocument | null;
}

/**
 * Selects the brand document out of the composite `{ styles, brand }` envelope.
 * Returns `null` when the body carries no brand (the improve-ds-agent feature
 * is off, so the GET returns the bare token tree). Exported for unit testing.
 */
export function selectBrandFromComposite(raw: unknown): DsBrandDocument | null {
  const brand = isRecord(raw) && 'brand' in raw ? raw.brand : null;
  return brand == null ? null : dsBrandDocument.parse(brand);
}

/**
 * Parses the raw composite GET body into the `{ styles, brand }` envelope both
 * the styles and brand surfaces read from. `styles` unwraps `{ styles }.styles`
 * (tolerating the bare-tree + null shapes); `brand` is the source document (or
 * `null` when the feature is off).
 */
export function parseDsGlobalStylesEnvelope(raw: unknown): DsGlobalStylesEnvelope {
  return { styles: parseDsGlobalStyles(raw), brand: selectBrandFromComposite(raw) };
}

/**
 * @public
 *
 * Imperative styles read — the token tree with the composite envelope unwrapped.
 * Returns `null` for a styles-less workspace (the ui_api can marshal an unset
 * styles object as JSON `null`; parcel types this `GlobalStyles | null` and
 * treats null as a clean "no styles yet" success; CON-7608 parity audit).
 */
export async function getDsGlobalStyles(workspaceId: number): Promise<DsGlobalStyles | null> {
  return (await getDsGlobalStylesComposite(workspaceId)).styles;
}

export const getDsGlobalStylesQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: dsGlobalStylesKeys.current(workspaceId),
    queryFn: () => getDsGlobalStylesComposite(workspaceId),
    // Parse the untrusted response once at the request boundary. Consumers can
    // then select either slice from one cache slot without handling `unknown`.
    //
    // Parcel parity (CON-7608 audit): global styles are a collaborative,
    // workspace-wide resource that teammates can change out of band, so parcel
    // never caches the GET (react-query default `staleTime: 0` + refetch-on-focus).
    // Match that here so a stale styles tree is never shown after a co-editor
    // saves. The references / status reads below stay on `STALE_TIME` — they are
    // transient / consumer-driven and don't share this collaborative-freshness
    // requirement.
    staleTime: 0,
  });

interface UseDsGlobalStylesOptions {
  queryConfig?: QueryConfig<typeof getDsGlobalStylesQueryOptions>;
}

/**
 * The workspace design-system read: the parsed composite `{ styles, brand }`.
 * The Global Styles page consumes this SAME hook off the SAME query for both
 * slices — its styles grid reads `.data.styles` and its Brand section reads
 * `.data.brand` — so one query backs the whole page. Each slice is `null` when
 * absent (styles-less workspace, or brand feature off).
 *
 * @public
 */
export const useDsGlobalStyles = ({ queryConfig }: UseDsGlobalStylesOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    ...getDsGlobalStylesQueryOptions(workspaceId),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* getDsGlobalStylesReferences — nodes referencing the global styles           */
/* ------------------------------------------------------------------------- */

/**
 * Returns the nodes that reference the workspace global styles ("used in"),
 * camelCased into the frontend's discriminated union (CON-7595). EMAIL/other
 * rows become `{ id, type }`; in-app rows carry the full row with their
 * Unix-seconds timestamps normalized to ISO — the shape the restored in-app
 * publish path (save flow + bulk-publish modal) consumes. See
 * `transformGlobalStylesReferences` in `entity.ts`.
 *
 * @public
 */
export async function getDsGlobalStylesReferences(
  workspaceId: number,
  includeInApp = false,
): Promise<GlobalStylesReference[]> {
  const items = await hydraUiApi.dsGlobalStyles.getReferences({
    params: { path: { environment_id: String(workspaceId) }, query: { include_in_app: includeInApp } },
  });
  return transformGlobalStylesReferences(items);
}

export const getDsGlobalStylesReferencesQueryOptions = (workspaceId: number, includeInApp = false) =>
  queryOptions({
    queryKey: dsGlobalStylesKeys.references(workspaceId, includeInApp),
    queryFn: () => getDsGlobalStylesReferences(workspaceId, includeInApp),
    staleTime: STALE_TIME,
  });

interface UseDsGlobalStylesReferencesOptions {
  includeInApp?: boolean;
  queryConfig?: QueryConfig<typeof getDsGlobalStylesReferencesQueryOptions>;
}

/** @public */
export const useDsGlobalStylesReferences = ({
  includeInApp = false,
  queryConfig,
}: UseDsGlobalStylesReferencesOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    ...getDsGlobalStylesReferencesQueryOptions(workspaceId, includeInApp),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* Status polls — single-domain (extract) and multi-source (process)           */
/* ------------------------------------------------------------------------- */

export interface DsGlobalStylesStatusParams {
  workflowId: string;
  runId: string;
}

/** @public */
export async function getDsGlobalStylesProcessStatus(
  workspaceId: number,
  params: DsGlobalStylesStatusParams,
): Promise<DsGlobalStylesProcessStatusResponse> {
  return hydraUiApi.dsGlobalStyles.getProcessStatus({
    params: {
      path: { environment_id: String(workspaceId) },
      query: { workflow_id: params.workflowId, run_id: params.runId },
    },
  });
}

export const getDsGlobalStylesProcessStatusQueryOptions = (workspaceId: number, params: DsGlobalStylesStatusParams) =>
  queryOptions({
    queryKey: dsGlobalStylesKeys.processStatus(workspaceId, params.workflowId, params.runId),
    queryFn: () => getDsGlobalStylesProcessStatus(workspaceId, params),
    staleTime: 0,
  });

interface UseDsGlobalStylesProcessStatusOptions {
  params: DsGlobalStylesStatusParams;
  queryConfig?: QueryConfig<typeof getDsGlobalStylesProcessStatusQueryOptions>;
}

/**
 * Non-suspense poll for the multi-source extraction: the caller gates the fetch
 * with `queryConfig: { enabled }` and drives cadence with
 * `queryConfig: { refetchInterval }`, so polling is not baked in here.
 *
 * @public
 */
export const useDsGlobalStylesProcessStatus = ({ params, queryConfig }: UseDsGlobalStylesProcessStatusOptions) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({
    ...getDsGlobalStylesProcessStatusQueryOptions(workspaceId, params),
    ...queryConfig,
  });
};
